import React, { useEffect } from 'react';
import getLogger from '@ignitesales/logger-es';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import QuestionHeader from './QuestionHeader';
import useNeuroState, {answerChanged, useDispatch} from '../state';
import { lowercaseFirstChar, QuestionShape } from '../utils';
import {useQuestionContext} from './QuestionContext';

const logger = getLogger({ component: 'Question' });

function Question({
  component: Component,
  page,
  question,
  questionGroup,
  ...rest
}) {
  const dispatch = useDispatch();
  const { answers } = useNeuroState();
  const { getCustomClasses } = useQuestionContext();
  const answerValue = answers[question.appkey] || [];

  const classes = classnames(
    getCustomClasses(question),
    'question',
    `question--${lowercaseFirstChar(question.type)}`,
    `${lowercaseFirstChar(question.type)}-question`,
    {
      ['question--error']: question.errors
    }
  );

  useEffect(() => prepopulateAnswer(), []);
  const prepopulateAnswer = () => {
    const { value = [] } = answerValue;
    const { answers } = page;
    if (
      (!value || (Array.isArray(value) && !value.length)) &&
      question?.attributes?.prepopulate
    ) {
      const prepopulateAppkey = question.attributes.prepopulate;
      const prepopulateAnswer = answers[prepopulateAppkey];
      logger.debug(
        `Prepopulate request found: ${question.appkey}:`,
        prepopulateAnswer || 'NO PRE-EXISTING VALUE FOUND'
      );
      if (prepopulateAnswer?.value) {
        saveChangedValue(prepopulateAnswer.value);
      }
    }
  };

  const saveChangedValue = (value) => {
    logger.debug('valueChange() page object', page);
    const finalValue = typeof value === 'string' ? [value] : value;
    const calculatedQuestions = (page.questionGroups || []).reduce(
      (list, currentGroup) => {
        return list.concat(
          currentGroup.questions.filter(
            (q) => q.type.toLowerCase() === 'calculated'
          )
        );
      },
      []
    );
    dispatch(
      answerChanged({
        question,
        questionGroup,
        value: finalValue,
        calculatedQuestions,
      })
    );
  };

  const onValueChange = (value) => {
    if (question?.attributes?.valueChanged) {
      question.attributes.valueChanged({ value, saveChangedValue });
    } else {
      saveChangedValue(value);
    }
  };

  return (
    <section id={`${question.appkey}-question`} className={classes}>
      <QuestionHeader question={question} />
      <Component
        {...rest}
        page={page}
        question={question}
        questionGroup={questionGroup}
        value={answerValue}
        valueChanged={onValueChange}
      />
    </section>
  );
}

Question.propTypes = {
  component: PropTypes.any,
  page: PropTypes.any,
  question: QuestionShape,
  questionGroup: PropTypes.any,
};

export default Question;
